import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getOrderProviderExternalErrorsChartData } from 'config/requests/orderProviderExternalErrors';
import { OrderProviderExternalErrorsWidgetsNames } from 'pages/Monitoring/OrderProviderExternalErrors/constants/widgetsNames';
import { OrderProviderExternalErrorsFilterValues } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsFilter/OrderProviderExternalErrorsFilter';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ErrorData } from 'types/embedded/error';
import { OrderProviderExternalErrorChartData } from 'types/embedded/orderProviderExternalErrors';
import { getId } from 'utils';
import { getMessageFromErrorBody } from 'utils/getMessageFromErrorBody';

const DEFAULT_WIDGETS = [
  OrderProviderExternalErrorsWidgetsNames.RatioByProviders,
  OrderProviderExternalErrorsWidgetsNames.ReasonsByPeriod,
];

type ReturnValue = {
  data?: OrderProviderExternalErrorChartData;
  isLoading: boolean;
  error?: string;
};

export function useGetOrderProviderExternalErrorsChartData(
  filters: OrderProviderExternalErrorsFilterValues,
  widgets: OrderProviderExternalErrorsWidgetsNames[] = DEFAULT_WIDGETS,
): ReturnValue {
  const translate = useTypedTranslate();

  const { data, isLoading, isError, error } = useQuery<
    OrderProviderExternalErrorChartData,
    AxiosResponse<ErrorData> | undefined
  >({
    queryKey: [
      'provider_external_error/chart_data',
      filters.merchant,
      filters.gateway,
      filters.appliedDate,
      filters.createdAt,
      filters.timezone,
      filters.providerExternalError,
      filters.method,
      filters.order,
      widgets,
    ],
    queryFn: () =>
      getOrderProviderExternalErrorsChartData({
        widgets,
        dateFrom: filters?.createdAt?.gte,
        dateTo: filters?.createdAt?.lte,
        timezone: filters?.timezone,
        merchant: filters?.merchant?.map((merchant) => getId(merchant)),
        provider: filters?.provider?.map((provider) => getId(provider)),
        gateway: filters?.gateway
          ? filters?.gateway.map((gateway) => getId(gateway))
          : filters?.order?.gateway?.map((gateway) => getId(gateway)),
        method: filters?.method?.map((method) => getId(method)),
        direction: filters?.order?.direction,
        category: filters?.providerExternalError?.category?.map((category) => getId(category)),
        rejectReason: filters?.providerExternalError?.reason
          ? [filters?.providerExternalError?.reason]
          : undefined,
        isCritical: filters?.providerExternalError?.isCritical
          ? Boolean(Number(filters?.providerExternalError?.isCritical))
          : undefined,
      }),
    enabled: Object.keys(filters).length > 0,
    retry: false,
  });

  const errorMessage = isError
    ? getMessageFromErrorBody(error?.data ?? null) || translate('app.common.serverError')
    : undefined;

  return { data, isLoading, error: errorMessage };
}
